// ─────────────────────────────────────────────────────────────────────────
// UITRUSTING module — credit-gestuurde uitrustingbestellingen (kleding/
// gereedschap/PBM) met teamleider-beoordeling en Van Ysselt-bestelmail.
// DTO's spiegelen de Prisma-modellen UitrustingArtikel/UitrustingVariant/
// UitrustingPakket(Regel)/CreditSaldo/CreditMutatie/UitrustingBestelling(Regel).
//
// Conventies (gelijk aan de rest van packages/types):
//   - Decimal-velden (prijsExclBtw) komen over de wire als string.
//   - Nullable DB-kolommen worden `T | null` (niet optioneel).
//   - Enums als string-unions die de Prisma-enums spiegelen.
// ─────────────────────────────────────────────────────────────────────────

import { z } from 'zod'
import { fotoCacheVersie } from './voorraad'
import type { UitrustingArtikelDoelgroepDTO } from './uitrusting-doelgroep'
import type { Leverwijze, RegelVoorraadInfo } from './leverwijze'
import type { DubbelMarkeringDTO, UitrustingRegelStatus } from './uitrusting-verwerking'
// batch-65 uitrusting-flow (FO-480)
import type { UitrustingLeveringDTO } from './uitrusting-levering'
// batch-69 bestelflow (FO-512) — de status-tijdlijn op het bestellingdetail.
import type { UitrustingStatusTijdlijnDTO } from './uitrusting-bestelflow'

// ─── Enums (matching Prisma enums as string unions) ─────────────────────────

// batch-69 samenstellen (FO-510 §C) — MERCHANDISE: STREEP-producten (mok,
// koeltas, speaker, powerbank). Altijd eigendom van de medewerker: nooit
// bruikleen, nooit op de inleverlijst.
export type UitrustingCategorie = 'KLEDING' | 'GEREEDSCHAP' | 'PBM' | 'VEILIGHEID' | 'MERCHANDISE'

/** Vaste volgorde van de categorieën — bron voor de Zod-enum + de matrix-UI. */
export const UITRUSTING_CATEGORIE_VALUES = [
  'KLEDING',
  'GEREEDSCHAP',
  'PBM',
  'VEILIGHEID',
  'MERCHANDISE',
] as const satisfies readonly UitrustingCategorie[]

export type BezorgKeuze = 'THUIS' | 'AFHALEN'

export type UitrustingBestellingStatus =
  | 'CONCEPT'
  | 'INGEDIEND'
  | 'GOEDGEKEURD'
  | 'AFGEKEURD'
  | 'VERZONDEN_LEVERANCIER'
  | 'VERWERKT'
  | 'GELEVERD'
  | 'GEANNULEERD'

export const UITRUSTING_CATEGORIE_LABELS: Record<UitrustingCategorie, string> = {
  KLEDING:     'Kleding',
  GEREEDSCHAP: 'Gereedschap',
  PBM:         'PBM',
  VEILIGHEID:  'Veiligheid',
  MERCHANDISE: 'Merchandise',
}

export const BEZORG_KEUZE_LABELS: Record<BezorgKeuze, string> = {
  THUIS:   'Thuisbezorgen',
  AFHALEN: 'Afhalen',
}

export const UITRUSTING_BESTELLING_STATUS_LABELS: Record<UitrustingBestellingStatus, string> = {
  CONCEPT:               'Concept',
  INGEDIEND:             'Ingediend',
  GOEDGEKEURD:           'Goedgekeurd',
  AFGEKEURD:             'Afgekeurd',
  VERZONDEN_LEVERANCIER: 'Verzonden naar leverancier (oud)',
  VERWERKT:              'Verwerkt',
  GELEVERD:              'Geleverd',
  GEANNULEERD:           'Geannuleerd',
}

export const UITRUSTING_BESTELLING_STATUS_BADGE: Record<
  UitrustingBestellingStatus,
  'secondary' | 'info' | 'warning' | 'success' | 'danger'
> = {
  CONCEPT:               'secondary',
  INGEDIEND:             'warning',
  GOEDGEKEURD:           'info',
  AFGEKEURD:             'danger',
  VERZONDEN_LEVERANCIER: 'info',
  VERWERKT:              'info',
  GELEVERD:              'success',
  GEANNULEERD:           'secondary',
}

// ─── DTOs ──────────────────────────────────────────────────────────────────

/**
 * Spec 2026-08-25 — voorraad-bron van één maat-variant, batch-berekend in de
 * beheer-respons (backoffice). `verwijderd` = de bron is soft-deleted: de
 * koppel-UI toont dan "voorraadartikel verwijderd" met koppel/maak-aan.
 */
export interface UitrustingVariantVoorraadDTO {
  voorraadArtikelId: string
  artikelNummer: string
  naam: string
  maat: string | null
  /** Totaal over alle locaties (incl. gereserveerd). */
  totaalSaldo: number
  /** Vrij te vergeven (totaal minus reserveringen). */
  vrij: number
  /** Locatie met het meeste vrije saldo, of null bij 0. */
  locatieNaam: string | null
  verwijderd: boolean
  /**
   * True = deze bron komt van de FO-321-koppeling op ARTIKELniveau (gereedschap),
   * niet van deze maat zelf. De catalogus-cel toont 'm dan alleen ter info: koppel
   * en ontkoppel gaan over de artikel-koppeling, niet over deze maat.
   */
  geerfdVanArtikel: boolean
  /**
   * `VoorraadArtikel.actief`. False = niet meer uitgeefbaar: saldo en beleid
   * blijven zichtbaar, maar reserveren levert niets meer op (het scherm toont
   * "gedeactiveerd").
   */
  actief: boolean
}

export interface UitrustingVariantDTO {
  id: string
  artikelnummer: string
  kleur: string | null
  maat: string | null
  /** 'boven'|'broek'|'schoen'|'handschoen'|'helm'|'overig' */
  maatType: string | null
  /**
   * Decimal komt over de wire als string - UI parset waar nodig. `null` in de
   * portal-respons: de euro-verkoopprijs is interne data en wordt voor monteurs
   * gestript (rol bepaalt het scherm); backoffice krijgt altijd de waarde.
   */
  prijsExclBtw: string | null
  credits: number
  actief: boolean
  /**
   * Spec 2026-08-25 — voorraad-bron van deze maat (variant-koppeling), of null
   * als de variant geen eigen voorraadartikel heeft. Portal-respons: altijd null
   * (interne magazijndata).
   */
  voorraad: UitrustingVariantVoorraadDTO | null
}

export interface UitrustingArtikelDTO {
  id: string
  categorie: UitrustingCategorie
  merk: string | null
  naam: string
  omschrijving: string | null
  /**
   * Artikelfoto. Twee vormen: een blob-storage-key (geüploade foto → stream via
   * de same-origin `/…/foto`-proxy) OF een absolute http(s)-URL (uit de voorraad-
   * bron, bv. een leverancier-/shopping-afbeelding → direct als `<img src>`).
   * Gebruik `artikelFotoSrc()` om de juiste `src` te kiezen; anders geeft de
   * stream-proxy 404 op een externe URL (die is geen blob-key).
   */
  fotoPath: string | null
  actief: boolean
  /**
   * Op-maat / op-sterkte artikel (otoplastieken, bril op sterkte): geen
   * maat-variant, vereist vrije tekst (sterkte/recept) en telt standaard niet
   * tegen het credit-saldo (variant credits = 0).
   */
  opMaat: boolean
  // FO-059 — leverancier per artikel (Client type=LEVERANCIER) + commerciële
  // velden. Elk artikel hoort een leverancier te hebben: de bestelmail groepeert
  // hierop (geen globale Van Ysselt-fallback meer).
  leverancierId: string | null
  leverancierNaam: string | null
  leverancierArtNr: string | null
  /**
   * Optionele extra bestelmail-ontvanger(s), komma-gescheiden. Worden toegevoegd
   * aan de leveranciersmail van dit artikel (bv. op-maat-artikelen die intern
   * moeten meelezen). null/leeg = alleen de leverancier ontvangt.
   */
  extraBestelEmails: string | null
  /** Inkoopprijs excl. btw (Decimal als string), of null. Bij een gekoppeld item
   *  geërfd uit de voorraad-bron (read-only); voor de portal gestript (intern). */
  inkoopPrijs: string | null
  minimumVoorraad: number
  /**
   * Ruwe bestelbaar-vlag (catalogus-eigen beleid). LET OP: de effectieve
   * bestelbaarheid is `bestelbaar && !bronGedeactiveerd` — een gedeactiveerde
   * voorraad-bron maakt het item automatisch niet-bestelbaar (afgeleid, geen
   * stored mutatie). Gebruik `isCatalogusItemBestelbaar()` i.p.v. dit veld direct.
   */
  bestelbaar: boolean
  /**
   * batch-41 (FO-359) — uitgifte-only: het artikel bestaat alleen voor
   * backoffice-flows (uitgifte-set, bestelvoorstel, pakket-toekenning) en
   * verschijnt nooit in de portal-catalogus; concept/indienen weigert hard en
   * de TL-beoordeling kan de regel niet goedkeuren. Complete sets (Packout).
   */
  alleenUitgifte: boolean
  // ── FO-321 (batch-36) — voorraad-bron-koppeling (één waarheid) ──────────────
  /** Gekoppeld voorraad-artikel (bron), of null (zelfstandig catalogus-item). */
  voorraadArtikelId: string | null
  /** Artikelnummer van de bron (ART-xxxx), read-only display. null = niet gekoppeld. */
  voorraadArtikelNummer: string | null
  /**
   * True zolang aan een voorraad-bron gekoppeld: naam/merk/omschrijving/foto/
   * leverancier/artikelnummer/inkoopprijs zijn dan GEËRFD uit de bron (read-only
   * in de catalogus-editor, met "ontkoppel om af te wijken").
   */
  gekoppeld: boolean
  /**
   * batch-57 (FO-419) - erft dit item zijn presentatievelden uit de bron?
   * False = de koppeling dient alleen de voorraad (het voorraad-artikel is uit
   * dit item gemaakt of er automatisch aan gematcht); het item blijft dan gewoon
   * bewerkbaar en `gekoppeld` staat op false. True = de FO-321-erf-koppeling.
   */
  voorraadBronErft: boolean
  /**
   * True als de gekoppelde bron gedeactiveerd is (`VoorraadArtikel.actief=false`
   * of soft-deleted): het item is dan NIET bestelbaar (afgeleid) en het dagelijkse
   * sync-signaal benoemt het. Heractiveren van de bron herstelt automatisch.
   */
  bronGedeactiveerd: boolean
  /** Herkomst-tags ("Servicekoffer", "Onderhoudskar 7-lades", "Voorraad"), filterbaar. */
  herkomstLabels: string[]
  varianten: UitrustingVariantDTO[]
  /**
   * FO-149 — afgeleid doelgroep-blok (beperkt/regels/effectiefLeeg). Alleen in
   * de beheer-respons (backoffice); de portal krijgt dit blok niet — een
   * niet-beschikbaar artikel bestaat daar simpelweg niet.
   */
  doelgroep?: UitrustingArtikelDoelgroepDTO
}

/**
 * FO-321 — de ENIGE bron voor "kan dit catalogus-item besteld worden": de
 * catalogus-eigen bestelbaar-vlag ÉN (indien gekoppeld) een actieve voorraad-
 * bron. Een gedeactiveerde bron maakt het item niet-bestelbaar zonder de
 * opgeslagen vlag te muteren (afgeleid; heractiveren herstelt).
 */
export function isCatalogusItemBestelbaar(
  artikel: Pick<UitrustingArtikelDTO, 'bestelbaar' | 'bronGedeactiveerd'>,
): boolean {
  return artikel.bestelbaar && !artikel.bronGedeactiveerd
}

/**
 * Kies de `<img src>` voor een artikelfoto. `fotoPath` kan twee vormen hebben:
 *   - een absolute http(s)-URL (uit de voorraad-bron, bv. een leverancier-/
 *     shopping-afbeelding) → direct renderen; de stream-proxy zou hierop 404'en
 *     want het is geen blob-key.
 *   - een blob-storage-key (geüploade foto) → via de same-origin `proxyBase`-
 *     proxy (`…/uitrusting/artikelen/:id/foto`) die de Bearer server-side bijzet.
 * Geeft `null` terug als er geen foto is (caller toont dan de placeholder).
 * `proxyBase` = het pad-prefix vóór `/:id/foto` (backoffice `/api/uitrusting/
 * artikelen`, portal `/api/v1/uitrusting/artikelen`).
 */
export function artikelFotoSrc(
  artikel: Pick<UitrustingArtikelDTO, 'id' | 'fotoPath'>,
  proxyBase: string,
): string | null {
  if (!artikel.fotoPath) return null
  if (/^https?:\/\//i.test(artikel.fotoPath)) return artikel.fotoPath
  // `?v=`: de proxy-URL is stabiel per artikel en publiek gecachet (1u); de
  // waarde-hash maakt de URL vers zodra de key wijzigt (upload = unieke key).
  return `${proxyBase}/${artikel.id}/foto?v=${fotoCacheVersie(artikel.fotoPath)}`
}

export interface UitrustingPakketRegelArtikelDTO {
  id: string
  naam: string
  categorie: UitrustingCategorie
  merk: string | null
  /** Indicatieve credits (eerste actieve variant); werkelijk = afhankelijk van de maat. */
  indicatieveCredits: number | null
  /** Maatdimensie afgeleid uit de varianten ('boven'|'broek'|…), of null bij one-size. */
  maatType: string | null
  aantalVarianten: number
}

/**
 * Pakketregel = een ARTIKEL (maat-agnostisch) + aantal. De maat-specifieke
 * variant wordt per medewerker bij toekenning afgeleid uit de opgeslagen maten.
 */
export interface UitrustingPakketRegelDTO {
  id: string
  artikelId: string
  aantal: number
  artikel?: UitrustingPakketRegelArtikelDTO
}

export interface UitrustingPakketDTO {
  id: string
  naam: string
  categorie: UitrustingCategorie | null
  omschrijving: string | null
  actief: boolean
  regels: UitrustingPakketRegelDTO[]
  /**
   * Indicatief: som van een representatieve variant per regel × aantal (Decimal
   * als string). `null` in de portal-respons: euro-bedragen worden voor monteurs
   * gestript (rol bepaalt het scherm); backoffice krijgt altijd de waarde.
   */
  afgeleidePrijs: string | null
  /** Indicatief: som van een representatieve variant per regel × aantal. */
  afgeleideCredits: number
}

export interface CreditSaldoDTO {
  id: string
  employeeId: string
  jaar: number
  kwartaal: number
  toegekend: number
  gebruikt: number
  gereserveerd: number
  /** Afgeleid: toegekend - gebruikt - gereserveerd. */
  beschikbaar: number
}

export interface CreditMutatieDTO {
  id: string
  jaar: number
  kwartaal: number
  /** 'kwartaaltoekenning'|'reservering'|'afname'|'vrijgave'|'verval'|'correctie'|'retour' */
  richting: string
  credits: number
  bron: string
  bestellingId: string | null
  aangemaaktDoor: string
  toelichting: string | null
  createdAt: string
  /**
   * batch-61 (FO-445) — ledger-snapshot op het mutatiemoment (toegekend - gebruikt
   * vóór/ná) en het moment waarop de mutatie met Van Ysselt is gedeeld. Alleen
   * gevuld voor mutaties die een Van Ysselt-bericht dragen; anders null.
   */
  saldoVoor: number | null
  saldoNa: number | null
  gereserveerdNa: number | null
  gedeeldOp: string | null
}

/**
 * Bevroren snapshot van de variant t.t.v. bestellen (UitrustingBestellingRegel
 * .variantSnapshot, Json-kolom). prijsExclBtw is een Decimal en blijft daarom
 * een string in de snapshot.
 */
export interface UitrustingVariantSnapshot {
  artikelnummer: string
  naam: string
  kleur: string | null
  maat: string | null
  prijsExclBtw: string
  credits: number
  /** True voor op-maat-artikelen (otoplastieken, bril op sterkte). Optioneel: oude snapshots missen dit veld. */
  opMaat?: boolean
}

export interface UitrustingBestellingRegelDTO {
  id: string
  variantId: string
  variantSnapshot: UitrustingVariantSnapshot
  aantal: number
  creditsPerStuk: number
  bedrukking: boolean
  /** Vrije tekst (sterkte/recept/opmerkingen) voor op-maat-artikelen. */
  opMaatToelichting: string | null
  /**
   * Categorie van het bestelde artikel, bevroren t.t.v. bestellen. Bepaalt of de
   * regel credit-vrij is (PBM/VEILIGHEID tellen niet mee in creditsTotaal). null
   * voor bestelregels van vóór deze feature.
   */
  categorie: UitrustingCategorie | null
  /**
   * FO-149 — alleen in de beoordeling-respons: true wanneer het artikel van
   * deze regel (rand-geval) inmiddels buiten de doelgroep van de besteller
   * valt. De hint waarschuwt de beoordelaar; blokkeert niet.
   */
  buitenDoelgroep?: boolean
  /**
   * batch-41 (FO-359) — alleen in de beoordeling-respons: true wanneer het
   * artikel van deze regel inmiddels uitgifte-only is. Anders dan de
   * doelgroep-hint blokkeert dit HARD: goedkeuren wordt server-side geweigerd
   * (403 ALLEEN_UITGIFTE); afkeuren blijft mogelijk (opschoning).
   */
  alleenUitgifte?: boolean
  /**
   * batch-42 (FO-373) — vastgelegde leverwijze-keuze (bij goedkeuring gezet).
   * null = nog niet beoordeeld of legacy (volledig extern, bestaand gedrag).
   */
  leverwijze?: Leverwijze | null
  /** batch-42 (FO-373) — hoeveel van `aantal` uit voorraad geleverd is (rest extern). */
  aantalVoorraad?: number
  /**
   * Verwerk-stap (2026-08) — leverstatus van deze regel. Deelleveringen zijn de
   * norm (voorraaddeel is er morgen, leverancier levert over drie weken), dus de
   * bestelling-brede status zegt niet genoeg. Ook de portal toont dit per regel;
   * het bevat geen interne gegevens (geen prijzen, geen leverancier).
   */
  regelStatus?: UitrustingRegelStatus
  /** Daadwerkelijk afgeleverde stuks van deze regel. */
  aantalGeleverd?: number
  /** Moment waarop de regel volledig geleverd was. */
  geleverdOp?: string | null
  /**
   * Catalogus-artikel achter de variant, plus het foto-pad. Nodig om de
   * artikelfoto naast de bestelregel te tonen (beoordelen en verwerken): een
   * beoordelaar herkent een broek sneller aan het plaatje dan aan het
   * artikelnummer. Bouw de src met `artikelFotoSrc`; die kiest tussen de
   * same-origin proxy en een externe URL.
   */
  artikelId?: string
  artikelFotoPath?: string | null
  /**
   * batch-42 (FO-373) — alleen in de beoordeling-respons: voorraad-/leverancier-
   * context (server-side batch) voor de LeverwijzeKiezer. Ontbreekt buiten de
   * beoordeling of als het artikel geen voorraad-koppeling heeft (dan alleen extern).
   */
  voorraad?: RegelVoorraadInfo
  /**
   * Verwerk-stap (2026-08) — deze medewerker heeft dit ARTIKEL al ergens staan:
   * in een lopende bestelling of in een levering van de laatste 90 dagen.
   * Waarschuwt, blokkeert niet. Aanwezig in de beoordeling- en verwerkings-
   * respons; elders afwezig.
   */
  dubbel?: DubbelMarkeringDTO | null
  /**
   * batch-65 uitrusting-flow (FO-479) — alleen op een CONCEPT: het artikel van
   * deze regel is niet (meer) bestelbaar voor de besteller (variant of artikel
   * uit de catalogus, buiten de doelgroep, alleen-uitgifte). Een herindiening
   * kopieert zo'n regel mét deze markering; indienen blokkeert tot hij weg is.
   */
  nietMeerBeschikbaar?: boolean
}

export interface UitrustingBestellingDTO {
  id: string
  employeeId: string
  status: UitrustingBestellingStatus
  bezorgKeuze: BezorgKeuze
  creditsTotaal: number
  vrijVeldVerzoek: string | null
  jaar: number
  kwartaal: number
  entityId: string
  ingediendOp: string | null
  beoordeeldDoorId: string | null
  beoordeeldOp: string | null
  beoordelingNotitie: string | null
  vanYsseltMailId: string | null
  /**
   * Geleverd melden (2026-08) — alleen gevuld bij een HANDMATIGE geleverd-
   * melding (backoffice); de automatische volledig-uit-voorraad-route (FO-373)
   * laat beide null. Terugzetten kan uitsluitend wanneer `geleverdOp` gevuld is.
   */
  geleverdOp: string | null
  geleverdDoorId: string | null
  /**
   * Verwerk-stap (2026-08): moment waarop de bestelling is samengesteld en
   * bevestigd. Voedt de stappenbalk in portal en backoffice.
   */
  verwerktOp: string | null
  verwerktDoorId: string | null
  createdAt: string
  regels: UitrustingBestellingRegelDTO[]
  employeeNaam?: string
  beoordeeldDoorNaam?: string
  geleverdDoorNaam?: string
  verwerktDoorNaam?: string
  entityNaam?: string
  /**
   * batch-61 (FO-445 §3B.8) — "gedeeld met Van Ysselt op {datum}". Alleen gevuld
   * wanneer de bestelling Van Ysselt-credits raakt (afname-mutatie met bericht-
   * delen); undefined/null = niet van toepassing (credit-vrij of andere
   * leverancier) of door de caller niet meegeladen.
   */
  vanYsseltDeling?: {
    gedeeld: boolean
    gedeeldOp: string | null
    bezorgfout: boolean
    wachtend: number
  } | null
  // ── batch-65 uitrusting-flow (FO-479) — herindiening ──────────────────────
  /** Herkomst: PORTAL (zelf besteld) of PAKKET (door HR toegekend). */
  bron?: UitrustingBestellingBron
  /** Deze bestelling is een aangepaste kopie van een afgekeurde bestelling. */
  herindieningVanId?: string | null
  /**
   * Herkomst, afkeurreden en wat er ten opzichte van de afgekeurde bestelling
   * gewijzigd is. Gevuld op het detail en in de beoordeling; elders afwezig.
   */
  herindiening?: UitrustingHerindieningDTO | null
  /**
   * Op een AFGEKEURDE bestelling: de herindiening die eruit is voortgekomen
   * (link vanuit de historie). null = nog niet opnieuw ingediend.
   */
  herindiendAls?: { id: string; status: UitrustingBestellingStatus } | null
  // ── batch-65 uitrusting-flow (FO-480) — levering of afhalen ────────────────
  /**
   * De lever-/afhaalafspraken waar regels van deze bestelling in zitten (ook
   * afgeronde, als tijdlijn). Gevuld op het detail; elders afwezig.
   */
  leveringen?: UitrustingLeveringDTO[]
  // ── batch-69 bestelflow (FO-512) — claim + status-tijdlijn ─────────────────
  /** Wie de goedgekeurde bestelling heeft opgepakt (claim), of null. */
  opgepaktDoorId?: string | null
  opgepaktOp?: string | null
  opgepaktDoorNaam?: string | null
  /**
   * De status-tijdlijn (fase, stappen, per regel extern besteld / uit voorraad /
   * geleverd, gebeurtenissen). Gevuld op het detail: voor de aanvrager de
   * publieke weergave (zonder leverancier en interne notities), voor de
   * backoffice de volledige. Elders afwezig.
   */
  statusTijdlijn?: UitrustingStatusTijdlijnDTO
}

/**
 * Maatprofiel van een medewerker — bron voor de uitrusting-catalogus en de
 * bestelmail. Velden komen 1:1 uit Employee; alle maten (incl. `schoenmaat`)
 * zijn vrije tekst.
 */
export interface WerknemersMaatDTO {
  employeeId: string
  naam: string
  kledingmaatBoven: string | null
  kledingmaatBroek: string | null
  schoenmaat: string | null
  handschoenmaat: string | null
  helmmaat: string | null
  /** Vrije pasvorm-opmerking ("extra breed"); hoort niet in de maatvelden zelf. */
  maatOpmerking: string | null
}

/** Eén optie in een maat-keuzelijst. Spiegelt `MaatOptie` uit `@streep/utils`. */
export interface MaatOptie {
  /** Canonieke opslagwaarde, bv. "10", "48", "2XL". */
  waarde: string
  /** Weergave in de keuzelijst, bv. "10 (XL)". */
  label: string
}

/**
 * Keuzelijst per maatType, gevuld met de maten die de catalogus daadwerkelijk
 * voert. Voedt de maat-pickers op de profielschermen: wat hier niet in staat,
 * kan niemand bestellen. Een leeg array betekent "catalogus voert geen maten
 * voor dit type" (bv. alleen one-size) - het veld valt dan terug op vrije tekst.
 */
export interface MaatOptiesDTO {
  boven: MaatOptie[]
  broek: MaatOptie[]
  schoen: MaatOptie[]
  handschoen: MaatOptie[]
  helm: MaatOptie[]
}

/**
 * Uitrusting-instellingen (singleton) — de kledingleverancier voor de
 * maatwijziging-notificatie + de optionele reply-to voor bestelmails. De
 * bestelmail-ontvanger zelf zit per leverancier (UitrustingLeverancier).
 */
export interface UitrustingInstellingenDTO {
  /** UitrustingLeverancier die de maatwijziging-notificatie (kledingmaten) ontvangt. */
  maatwijzigingLeverancierId: string | null
  vanYsseltReplyTo: string | null
  /**
   * batch-62 uitrusting-inkoop (FO-466 §B.5) - dubbel-controle-venster rond de
   * pakbon-/factuurdatum (default 14 dagen vóór t/m 7 dagen ná).
   */
  dubbelVensterDagenVoor: number
  dubbelVensterDagenNa: number
}

/**
 * Resultaat van de uitrusting-bestelmail (FO-059): de bestelling wordt per
 * leverancier gegroepeerd, met één mail per leverancier. `mails` zijn de
 * aangemaakte mails; `waarschuwingen` benoemt leveranciers/artikelen die NIET
 * verstuurd konden worden (artikel zonder leverancier of leverancier zonder
 * e-mailadres). Er is bewust GEEN fallback-leverancier: elk artikel hoort een
 * leverancier met e-mailadres te hebben.
 */
export interface UitrustingBestelmailLeverancierResultDTO {
  leverancierId: string | null
  leverancierNaam: string | null
  aantalRegels: number
  mailId: string | null
  verzonden: boolean
  /** Reden waarom niet verzonden (bv. 'recipient-not-configured'), of null. */
  reden: string | null
}

export interface UitrustingBestelmailResultDTO {
  /** True als élke leverancier-groep een mail kreeg (geen waarschuwingen). */
  allesVerzonden: boolean
  mails: UitrustingBestelmailLeverancierResultDTO[]
  waarschuwingen: string[]
  /**
   * batch-42 (FO-373) — aantal bestelregels met een extern-deel (aantalExtern
   * > 0). 0 = volledig uit voorraad geleverd -> er hoefde geen bestelmail uit
   * (de route zet de bestelling direct op GELEVERD, geen waarschuwing).
   */
  externRegels: number
}

/**
 * Vaste namen van de standaard-leveranciers (Client type=LEVERANCIER). Eén bron
 * voor de seed, de catalogus-import-default en de bestelmail-routing.
 */
export const VANYSSELT_LEVERANCIER_NAAM = 'Van Ysselt'
export const PEHAVO_LEVERANCIER_NAAM = 'Pehavo'
export const PEHAVO_LEVERANCIER_EMAIL = 'T.Schoonenberg@pehavo.nl'

// ─── FO-117 — categorie -> leverancier-routing ──────────────────────────────

/**
 * Eén categorie -> leverancier-koppeling (matrix-rij) voor de bestelmail-routing
 * (FO-117). De backoffice toont één rij per categorie; `leverancierId: null`
 * betekent "geen koppeling" -> die categorie valt terug op de default-leverancier
 * (Van Ysselt). De cascade per bestelregel: artikel-override -> deze koppeling ->
 * default.
 */
export interface UitrustingCategorieLeverancierDTO {
  categorie: UitrustingCategorie
  leverancierId: string | null
  leverancierNaam: string | null
}

/**
 * Upsert van één categorie -> leverancier-koppeling. `leverancierId: null` wist de
 * koppeling. Gedeeld tussen de API (`.safeParse`) en het backoffice-form
 * (client-side validatie), zodat het form niets kan sturen dat de API weigert.
 */
export const UpsertCategorieLeverancierSchema = z.object({
  categorie: z.enum(UITRUSTING_CATEGORIE_VALUES),
  leverancierId: z.string().min(1, 'Kies een leverancier').nullable(),
})
export type UpsertCategorieLeverancierInput = z.infer<typeof UpsertCategorieLeverancierSchema>

// ─── Spec 2026-08-25 — variant-voorraadkoppeling (API `.safeParse` + form) ────

export const KoppelVariantVoorraadSchema = z.object({
  voorraadArtikelId: z.string().min(1),
  /** Eenmalig naam/foto/inkoopprijs uit de voorraad overnemen in het catalogusartikel. */
  overname: z.boolean().optional(),
})
export type KoppelVariantVoorraadInput = z.infer<typeof KoppelVariantVoorraadSchema>

export interface MaakOntbrekendeVoorraadResultDTO {
  aangemaakt: number
  overgeslagen: number
}

// ─── batch-65 uitrusting-flow (FO-479) — afgekeurde bestelling opnieuw indienen ─

/** Herkomst van een bestelling (spiegelt de Prisma-enum `UitrustingBestellingBron`). */
export type UitrustingBestellingBron = 'PORTAL' | 'PAKKET'

/**
 * Soorten wijziging van een regel tussen de afgekeurde bestelling en de
 * herindiening. Eén regel kan meerdere soorten tegelijk dragen (andere maat én
 * ander aantal).
 */
export const HERINDIENING_WIJZIGING_SOORTEN = [
  'TOEGEVOEGD',
  'VERWIJDERD',
  'AANTAL_GEWIJZIGD',
  'VARIANT_GEWIJZIGD',
  'BEDRUKKING_GEWIJZIGD',
  'TOELICHTING_GEWIJZIGD',
] as const
export type HerindieningWijzigingSoort = (typeof HERINDIENING_WIJZIGING_SOORTEN)[number]

/** Eén kant (oud of nieuw) van een gewijzigde regel. */
export interface HerindieningRegelVersieDTO {
  variantId: string
  maat: string | null
  kleur: string | null
  aantal: number
  bedrukking: boolean
  opMaatToelichting: string | null
}

export interface HerindieningRegelWijzigingDTO {
  soorten: HerindieningWijzigingSoort[]
  /** Artikelnaam uit de bevroren snapshot (nieuw als die er is, anders oud). */
  naam: string
  /** Stand in de afgekeurde bestelling; null bij een toegevoegde regel. */
  van: HerindieningRegelVersieDTO | null
  /** Stand in de herindiening; null bij een verwijderde regel. */
  naar: HerindieningRegelVersieDTO | null
  /** Leesbare NL-regel, bv. "Werkbroek (maat 52): aantal van 3 naar 2". */
  omschrijving: string
}

/**
 * Het verschil tussen een afgekeurde bestelling en haar herindiening. Puur
 * afgeleid (`bepaalHerindieningDiff` in `@streep/utils`), nooit opgeslagen: de
 * herindiening kan tot het indienen nog wijzigen.
 */
export interface HerindieningDiffDTO {
  regels: HerindieningRegelWijzigingDTO[]
  /** Aantal regels dat exact gelijk bleef. */
  ongewijzigd: number
  /** Bezorgkeuze gewijzigd (van -> naar); null = gelijk gebleven. */
  bezorgKeuze: { van: BezorgKeuze; naar: BezorgKeuze } | null
  vrijVeldVerzoekGewijzigd: boolean
  /** False = inhoudelijk identiek aan de afgekeurde bestelling. */
  gewijzigd: boolean
}

export interface UitrustingHerindieningDTO {
  /** De afgekeurde bestelling waar deze herindiening uit voortkomt. */
  vanId: string
  /** De afkeurnotitie (reden) van de beoordelaar. */
  reden: string | null
  afgekeurdOp: string | null
  afgekeurdDoorNaam: string | null
  diff: HerindieningDiffDTO
}
